import React, { useState, useEffect, useCallback } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useNavigate } from "react-router-dom";
import { getter } from "@progress/kendo-react-common";
import { useDispatch, useSelector } from "react-redux";
import CustomCellClick from "../Grid/CustomCellClick";
import CustomCell from "../Grid/CustomCell";
import { loadTicketTaskGridData } from "../../handlers/TicketTask";
import useReloadButton from "../../CustomHooks/useReloadButton";
import { useKendoGridHandlers } from "../../CustomHooks/useKendoGridHandlers";
import {
  setCurrentTicketNo,
  setSearchFrom,
  setTicketComment,
} from "../../store/reducers/SharedLayoutDataSlice";
import useHandlePageChange from "../../CustomHooks/useHandlePageChange";
import { setCurrentPage } from "../../store/reducers/TicketTaskFilter";
import { TICKET_TASK_FILTERS } from "../../constants/TicketTaskFilter";

const DATA_ITEM_KEY = "SEQ_NUM";
const SELECTED_FIELD = "selected";

const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO_TICKET = {
  action: "SEARCHTICKETS",
  className: "DASHBOARD",
  pageSize: 20,
  currentPageNumber: 1,
  sortName: "CREATED_DATE",
  sortOrder: "desc",
  gridHeight: "300px",
};

export const DEFAULT_GRID_INFO_TASK = {
  action: "SEARCHTASKS",
  className: "TASK",
  pageSize: 20,
  currentPageNumber: 1,
  sortName: "CREATED_DATE",
  sortOrder: "desc",
  gridHeight: "300px",
};

const PAGER_SETTINGS = {
  info: true,
  type: "input",
  previousNext: true,
  pageSize: 4,
  refresh: true,
};

const KendoTicketGrid = (props) => {
  const dispatch = useDispatch();
  const ticketTaskFilter = useSelector((state) => state.ticketTaskFilter);
  const sharedData = useSelector((state) => state.SharedLayoutData);

  const [gridData, setGridData] = useState([]);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [reloadToggle, setReloadToggle] = useState(false);

  const [gridState, setGridState] = useState({
    pageNumber:
      props.type === "Ticket"
        ? DEFAULT_GRID_INFO_TICKET.currentPageNumber
        : DEFAULT_GRID_INFO_TASK.currentPageNumber,
    pageSize:
      props.type === "Ticket"
        ? DEFAULT_GRID_INFO_TICKET.pageSize
        : DEFAULT_GRID_INFO_TASK.pageSize,
    sortName:
      props.type === "Ticket"
        ? DEFAULT_GRID_INFO_TICKET.sortName
        : DEFAULT_GRID_INFO_TASK.sortName,
    sortOrder:
      props.type === "Ticket"
        ? DEFAULT_GRID_INFO_TICKET.sortOrder
        : DEFAULT_GRID_INFO_TASK.sortOrder,
    sort: [
      {
        field:
          props.type === "Ticket"
            ? DEFAULT_GRID_INFO_TICKET.sortName
            : DEFAULT_GRID_INFO_TASK.sortName,
        dir:
          props.type === "Ticket"
            ? DEFAULT_GRID_INFO_TICKET.sortOrder?.toLowerCase()
            : DEFAULT_GRID_INFO_TASK.sortOrder?.toLowerCase(),
      },
    ],
  });

  const [selectedState, setSelectedState] = React.useState();
  const HandlePageChange = useHandlePageChange(setGridState, setCurrentPage);
  const { HandleDataStateChange } = useKendoGridHandlers(setGridState);

  useEffect(() => {
    loadTicketTaskGridData(ticketTaskFilter, gridState, sharedData).then(
      (response) => {
        setGridData(response.rows);
        setTotal(response.total);
      }
    );
  }, [ticketTaskFilter, gridState, reloadToggle, sharedData.headerRerender]);

  useReloadButton(setReloadToggle);

  const GetCustomCell = useCallback(
    (cellProps) => <CustomCell {...cellProps} {...cellProps.additionalProps} />,
    []
  );

  const CustomCellClicked = useCallback(
    (cellProps) => (
      <CustomCellClick
        {...cellProps}
        selectionChange={onSelectionChange}
        {...cellProps.additionalProps}
      />
    ),
    []
  );

  const ShowTaskDetail = (taskId, ticketType) => {
    dispatch(setCurrentTicketNo(taskId));
    dispatch(setSearchFrom("Ticket"));
    navigate("/ticketDetail", {
      state: {
        ticketNo: taskId,
        task: ticketType === "Training And Implementation",
        priorityClient: ticketTaskFilter.priorityClient,
      },
    });
  };

  const SelectedContentLength = () => {
    const selectedText = window.getSelection().toString();
    return selectedText.length > 0;
  };

  const onSelectionChange = React.useCallback(
    (event) => {
      const check = SelectedContentLength();
      if (!check) {
        const newSelectedState = getSelectedState({
          event,
          selectedState,
          dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
        const currentColumn = event.target._columns[event.endColIndex].field;
        if (currentColumn === "SEQ_NUM") {
          ShowTaskDetail(
            event.dataItems[event.endRowIndex][currentColumn],
            event.dataItems[event.endRowIndex].TICKET_TYPE
          );
        }
      }
    },
    [selectedState]
  );

  const CellTaskStatus = (props) => {
    if (props.dataItem.STATUS === "J") {
      return (
        <td>
          <div className="flex">
            <div className="ticket-overDue ticket-status">Pending/Over Due</div>
          </div>
        </td>
      );
    }
    if (props.dataItem.STATUS === "I") {
      return (
        <td>
          <div className="flex">
            <div className="ticket-inProgrees ticket-status">In Process</div>
          </div>
        </td>
      );
    }
    if (props.dataItem.STATUS === "O" || props.dataItem.STATUS === "I") {
      return (
        <td>
          <div className="flex">
            <div className="ticket-pending ticket-status">Pending</div>
          </div>
        </td>
      );
    }
    if (props.dataItem.STATUS === "R" || props.dataItem.STATUS === "r") {
      return (
        <td>
          <div className="flex">
            <div className="ticket-completed ticket-status">Completed</div>
          </div>
        </td>
      );
    }
    if (props.dataItem.STATUS === "Q" || props.dataItem.STATUS === "q") {
      return (
        <td>
          <div className="flex">
            <div className="ticket-pendingOnClient ticket-status">
              Pending On Client
            </div>
          </div>
        </td>
      );
    }
  };

  const CellTicketStatus = (props) => {
    if (
      props.dataItem.STATUS === "O" ||
      props.dataItem.STATUS === "o" ||
      props.dataItem.STATUS === "Open"
    ) {
      props.dataItem.STATUS = "Open";
      return (
        <td>
          <div className="flex">
            <div className="ticket-open w-100 m-0">{props.dataItem.STATUS}</div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "K" ||
      props.dataItem.STATUS === "k" ||
      props.dataItem.STATUS === "Reopened"
    ) {
      props.dataItem.STATUS = "Reopened";
      return (
        <td>
          <div className="flex">
            <div className="ticket-open w-100 m-0">{props.dataItem.STATUS}</div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "I" ||
      props.dataItem.STATUS === "i" ||
      props.dataItem.STATUS === "In Progress"
    ) {
      props.dataItem.STATUS = "In Progress";
      return (
        <td>
          <div className="flex">
            <div className="ticket-inProgrees ticket-status w-100 m-0">
              {props.dataItem.STATUS}
            </div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "Q" ||
      props.dataItem.STATUS === "q" ||
      props.dataItem.STATUS === "Pending On Client"
    ) {
      props.dataItem.STATUS = "Pending On Client";
      return (
        <td>
          <div className="flex">
            <div className="ticket-pendingOnClient ticket-status w-100 m-0">
              {props.dataItem.STATUS}
            </div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "R" ||
      props.dataItem.STATUS === "r" ||
      props.dataItem.STATUS === "Resolved"
    ) {
      props.dataItem.STATUS = "Resolved";
      return (
        <td>
          <div className="flex">
            <div className="ticket-resolved ticket-status w-100 m-0">
              {props.dataItem.STATUS}
            </div>
          </div>
        </td>
      );
    }
  };

  const CellComments = (props) => (
    <td className="Comments30 pl-4">
      <span className="textWrap" title={props.dataItem[props.field]}>
        {props.dataItem[props.field]}
      </span>
    </td>
  );

  const CellPracticeName = (props) => {
    if (props.dataItem.ENTITY_DESCRIPTION) {
      if (
        props.dataItem.ACTIVE_FLAG &&
        props.dataItem.ACTIVE_FLAG.toUpperCase() !== "Y"
      ) {
        return (
          <td
            className="Comments30 pl-4"
            title={props.dataItem.ENTITY_DESCRIPTION}
            style={{ color: "red" }}
          >
            {props.dataItem.ENTITY_DESCRIPTION}
          </td>
        );
      }
      if (
        props.dataItem.ACCOUNT_TYPE &&
        props.dataItem.ACCOUNT_TYPE.toUpperCase() === "LIVE"
      ) {
        return (
          <td
            className="Comments30 pl-4"
            title={props.dataItem.ENTITY_DESCRIPTION}
            style={{ color: "green" }}
          >
            {props.dataItem.ENTITY_DESCRIPTION}
          </td>
        );
      }
      return (
        <td
          className="Comments30 pl-4"
          title={props.dataItem.ENTITY_DESCRIPTION}
          style={{ color: "orange" }}
        >
          {props.dataItem.ENTITY_DESCRIPTION}
        </td>
      );
    }
  };

  const CellTicketCategory = (props) => {
    let data = TICKET_TASK_FILTERS.TICKET_CATEGORIES.find(
      (item) => item.value == props.dataItem.TICKET_CATEGORY
    );
    if (props.dataItem.TICKET_CATEGORY) {
      return (
        <td className="Comments30 pl-4" title={data?.label}>
          {data?.label}
        </td>
      );
    } else {
      return <td className="Comments30 pl-4"></td>;
    }
  };

  const CellSentenceCase = (props) => {
    const fieldName =
      props?.dataItem[props.field] != null
        ? props.dataItem[props.field]
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase())
        : null;

    return (
      <td className="Comments30 pl-4" title={fieldName}>
        {fieldName}
      </td>
    );
  };

  const MakeColumnTitle = () => {
    let columnName = "Task";
    if (ticketTaskFilter.type === "A") {
      columnName = "Ticket/Task #";
    } else if (ticketTaskFilter.type === "Task") {
      columnName = "Task #";
    } else {
      columnName = "Ticket #";
    }
    return columnName;
  };

  return (
    <div>
      <Tooltip openDelay={100} position="right" anchorElement="target">
        <Grid
          style={{ height: "calc(100vh - 182px)" }}
          scrollable="scrollable"
          total={total}
          skip={(ticketTaskFilter.currentPage - 1) * gridState.pageSize}
          pageSize={gridState.pageSize}
          filterable={false}
          sortable={{
            mode: "single",
            allowUnsort: false,
          }}
          sort={gridState.sort}
          selectedField={SELECTED_FIELD}
          data={gridData.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
          }))}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "multiple",
          }}
          pageable={PAGER_SETTINGS}
          onPageChange={HandlePageChange}
          onDataStateChange={HandleDataStateChange}
          onSelectionChange={onSelectionChange}
        >
          <GridColumn
            title="Status"
            field="STATUS"
            sortable
            cell={
              ticketTaskFilter.type === "Ticket"
                ? CellTicketStatus
                : CellTaskStatus
            }
            width={150}
          />
          <GridColumn
            title={MakeColumnTitle()}
            field="SEQ_NUM"
            cell={CustomCellClicked}
            sortable
            width={140}
          />
          <GridColumn
            title="Customer Id"
            field="ENTITY_SEQ_NUM"
            className="pl-4"
            sortable
            width={140}
          />
          <GridColumn
            title="Description"
            field="COMMENTS"
            sortable
            cell={CellComments}
            filterable={false}
            width={370}
          />
          <GridColumn
            title="Practice Name"
            field="ENTITY_DESCRIPTION"
            cell={CellPracticeName}
            sortable
            width={150}
          />
          <GridColumn
            title="Assignee"
            field="A_SHORT_NAME"
            cell={CellSentenceCase}
            className="pl-4"
            width={170}
          />
          <GridColumn
            title="Team"
            field="TEAMNAME"
            filterable={false}
            sortable
            width={150}
            className="pl-4"
          />
          <GridColumn
            title="Internal Assignee"
            field="INTERNAL_ASSIGNEE_SHORT_NAME"
            cell={CellSentenceCase}
            width={170}
          />
          <GridColumn
            title="Reporter"
            field="REPORTER_SHORT_NAME"
            cell={CellSentenceCase}
            className="pl-4"
            width={150}
          />
          <GridColumn
            title="Category"
            cell={CellTicketCategory}
            field="TICKET_CATEGORY"
            className="pl-4"
            width={150}
          />
          <GridColumn
            title="Priority"
            field="PRIORITY"
            filterable={false}
            sortable
            className="pl-4"
            width={150}
          />
          <GridColumn
            title="Target Date"
            field="TARGET_COMPLETION_DATE"
            cell={GetCustomCell}
            className="pl-4"
            width={150}
          />
          <GridColumn
            title="Created Date"
            field="CREATED_DATE"
            className="pl-4"
            cell={GetCustomCell}
            width={150}
          />
          <GridColumn
            title="Last Modified"
            field="LAST_UPDATE_DATE"
            className="pl-4"
            cell={GetCustomCell}
            width={150}
          />
        </Grid>
      </Tooltip>
    </div>
  );
};

export default KendoTicketGrid;
